import React, { useEffect, useMemo } from 'react';
import { Form, Input, Select, Alert } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    BANK_ACCOUNT_TYPES,
    BankAccountType,
    DspFinanceInfoType,
    FEDERAL_TAX_CLASSIFICATIONS,
    FederalTaxClassification,
    TAX_ID_TYPES,
    TRACK_FOR_1099_BY_CLASSIFICATION,
    TaxIdType,
    VENDOR_COUNTRY_US
} from '../../graphql/types/DspFinanceInfoType';

export interface DspFinanceInfoFormValues {
    vendorCountry?: string;
    federalTaxClassification?: FederalTaxClassification;
    taxIdType?: TaxIdType;
    taxIdNumber?: string;
    bankName?: string;
    accountType?: BankAccountType;
    accountNo?: string;
    routingNo?: string;
    vendorAddress?: string;
}

interface DspFinanceInfoFormProps {
    form: FormInstance<DspFinanceInfoFormValues>;
    financeInfo?: DspFinanceInfoType | null;
    disabled?: boolean;
}

/**
 * DSP 财务档案表单，总部端「财务信息」Tab 与 DSP Owner 端自助维护页共用。
 *
 * - trackFor1099 由服务端按联邦税务分类派生，此处只读展示，不提交
 * - 掩码态（masked=true）下税号与银行账号为服务端下发的掩码值，禁止编辑与提交
 */
const DspFinanceInfoForm: React.FC<DspFinanceInfoFormProps> = ({
    form,
    financeInfo,
    disabled = false
}) => {
    const { t } = useTranslation();
    const masked = financeInfo?.masked === true;
    const readOnly = disabled || masked;

    const classification = Form.useWatch('federalTaxClassification', form);

    const trackFor1099Text = useMemo(() => {
        if (!classification) return '-';
        return TRACK_FOR_1099_BY_CLASSIFICATION[classification]
            ? t('dspFinanceInfo.options.yes')
            : t('dspFinanceInfo.options.no');
    }, [classification, t]);

    useEffect(() => {
        if (!financeInfo) {
            form.resetFields();
            return;
        }
        form.setFieldsValue({
            vendorCountry: financeInfo.vendorCountry ?? VENDOR_COUNTRY_US,
            federalTaxClassification: financeInfo.federalTaxClassification ?? undefined,
            taxIdType: financeInfo.taxIdType ?? undefined,
            taxIdNumber: financeInfo.taxIdNumber ?? undefined,
            bankName: financeInfo.bankName ?? undefined,
            accountType: financeInfo.accountType ?? undefined,
            accountNo: financeInfo.accountNo ?? undefined,
            routingNo: financeInfo.routingNo ?? undefined,
            vendorAddress: financeInfo.vendorAddress ?? undefined
        });
    }, [financeInfo, form]);

    const alphanumericRule = {
        pattern: /^[A-Za-z0-9]+$/,
        message: t('dspFinanceInfo.messages.alphanumericOnly') as string
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            labelAlign="left"
            labelCol={{ flex: '200px' }}
            wrapperCol={{ flex: 1 }}
            labelWrap
            disabled={readOnly}
        >
            {masked && (
                <Alert
                    type="info"
                    showIcon
                    className="mb-4"
                    message={t('dspFinanceInfo.messages.maskedHint')}
                />
            )}

            <Form.Item
                name="vendorCountry"
                label={t('dspFinanceInfo.fields.vendorCountry')}
                initialValue={VENDOR_COUNTRY_US}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Select options={[{ value: VENDOR_COUNTRY_US, label: 'U.S.' }]} />
            </Form.Item>

            <Form.Item
                name="federalTaxClassification"
                label={t('dspFinanceInfo.fields.federalTaxClassification')}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Select
                    placeholder={t('dspFinanceInfo.placeholders.select') as string}
                    options={FEDERAL_TAX_CLASSIFICATIONS.map(value => ({
                        value,
                        label: t(`dspFinanceInfo.federalTaxClassification.${value}`)
                    }))}
                />
            </Form.Item>

            <Form.Item
                name="taxIdType"
                label={t('dspFinanceInfo.fields.taxIdType')}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Select
                    placeholder={t('dspFinanceInfo.placeholders.select') as string}
                    options={TAX_ID_TYPES.map(value => ({
                        value,
                        label: t(`dspFinanceInfo.taxIdType.${value}`)
                    }))}
                />
            </Form.Item>

            <Form.Item
                name="taxIdNumber"
                label={t('dspFinanceInfo.fields.taxIdNumber')}
                rules={[
                    { required: true, message: t('dspFinanceInfo.messages.required') as string },
                    alphanumericRule
                ]}
            >
                <Input maxLength={50} showCount placeholder={t('dspFinanceInfo.placeholders.input') as string} />
            </Form.Item>

            <Form.Item label={t('dspFinanceInfo.fields.trackFor1099')}>
                <span className="text-gray-700">{trackFor1099Text}</span>
                <div className="text-gray-400 text-xs">{t('dspFinanceInfo.messages.trackFor1099Auto')}</div>
            </Form.Item>

            <Form.Item
                name="bankName"
                label={t('dspFinanceInfo.fields.bankName')}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Input maxLength={50} showCount placeholder={t('dspFinanceInfo.placeholders.input') as string} />
            </Form.Item>

            <Form.Item
                name="accountType"
                label={t('dspFinanceInfo.fields.accountType')}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Select
                    placeholder={t('dspFinanceInfo.placeholders.select') as string}
                    options={BANK_ACCOUNT_TYPES.map(value => ({
                        value,
                        label: t(`dspFinanceInfo.accountType.${value}`)
                    }))}
                />
            </Form.Item>

            <Form.Item
                name="accountNo"
                label={t('dspFinanceInfo.fields.accountNo')}
                rules={[
                    { required: true, message: t('dspFinanceInfo.messages.required') as string },
                    alphanumericRule
                ]}
            >
                <Input maxLength={50} showCount placeholder={t('dspFinanceInfo.placeholders.input') as string} />
            </Form.Item>

            <Form.Item
                name="routingNo"
                label={t('dspFinanceInfo.fields.routingNo')}
                rules={[
                    { required: true, message: t('dspFinanceInfo.messages.required') as string },
                    alphanumericRule
                ]}
            >
                <Input maxLength={50} showCount placeholder={t('dspFinanceInfo.placeholders.input') as string} />
            </Form.Item>

            <Form.Item
                name="vendorAddress"
                label={t('dspFinanceInfo.fields.vendorAddress')}
                rules={[{ required: true, message: t('dspFinanceInfo.messages.required') as string }]}
            >
                <Input.TextArea
                    maxLength={200}
                    showCount
                    autoSize={{ minRows: 2, maxRows: 4 }}
                    placeholder={t('dspFinanceInfo.placeholders.input') as string}
                />
            </Form.Item>

        </Form>
    );
};

export default DspFinanceInfoForm;
